import { FormGroup, NgForm, ValidatorFn } from '@angular/forms';
import { cloneDeep, isEqual } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { BooleanOrUndefined, IFormValidationRules } from '../types';
import { allFieldsAreValid, getValidators, normalize } from '../util';

const PATH_DELIMITER = '.';

export abstract class ValidationService<T> {
  private isValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private previousValidatedForm: T | undefined = undefined;
  private previousNgFormValue: NgForm | undefined = undefined;

  public isValid$: Observable<boolean> = this.isValid as Observable<boolean>;

  private validatedObject: BehaviorSubject<BooleanOrUndefined<T> | undefined> =
    new BehaviorSubject<BooleanOrUndefined<T> | undefined>(undefined);

  public validatedObject$: Observable<BooleanOrUndefined<T> | undefined> =
    this.validatedObject.asObservable();

  private performValidation({ obj, form }: { obj: T; form: NgForm }) {
    const rules = this.determineRules(obj);

    this.checkControls({
      baseForm: form,
      rules,
      currentFormGroup: form.form,
    });

    const [allFieldsValid, validationObj] = allFieldsAreValid(
      obj,
      this.rulesFromDefinition(obj)
    );

    this.isValid.next(allFieldsValid);
    this.validatedObject.next(validationObj as BooleanOrUndefined<T>);
  }

  private determineRules(
    obj?: T
  ): [IFormValidationRules<T>, IFormValidationRules<T>?] {
    const mainRules = this.rulesFromDefinition(obj);

    if (!obj) {
      return [mainRules];
    }
    const normalizedRules = normalize(obj, mainRules);
    return [mainRules, normalizedRules];
  }

  private checkControls({
    baseForm,
    currentFormGroup,
    rules,
    path = '',
  }: {
    baseForm: NgForm;
    currentFormGroup: FormGroup;
    rules: [IFormValidationRules<T>, IFormValidationRules<T>?];
    path?: string;
  }): void {
    const [mainRules, normalizedRules] = rules;

    Object.entries(currentFormGroup.controls).forEach(([key, control]) => {
      const fullPath = `${path ? path + PATH_DELIMITER : ''}${key}`;
      const validators: ValidatorFn[] = getValidators(
        fullPath.split('.'),
        mainRules,
        normalizedRules
      );

      if (validators.length > 0) {
        const formElement = baseForm.form.get(fullPath);

        if (formElement) {
          formElement.setValidators(validators);
          formElement.updateValueAndValidity({
            onlySelf: false,
            emitEvent: true,
          });
        }
      }

      if ((control as FormGroup).controls) {
        this.checkControls({
          baseForm,
          currentFormGroup: control as FormGroup,
          rules,
          path: fullPath,
        });
        control.updateValueAndValidity({
          onlySelf: false,
          emitEvent: true,
        });
      }
    });
  }

  public validate(baseForm: NgForm, formObject?: T) {
    if (
      formObject &&
      baseForm &&
      (!isEqual(formObject, this.previousValidatedForm) ||
        !isEqual(baseForm.value, this.previousNgFormValue))
    ) {
      this.previousValidatedForm = cloneDeep(formObject);
      this.previousNgFormValue = cloneDeep(baseForm.value);

      setTimeout(
        () =>
          this.performValidation({
            obj: formObject,
            form: baseForm,
          }),
        0
      );
    }
  }

  // This accepts an object or we could get the source of truth from somewhere else.
  abstract rulesFromDefinition(input?: T): IFormValidationRules<T>;

  abstract additionalValidation(input?: T): boolean;
}
